import { Button, Loader } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import tvDisplay from "src/pages/Welcome/images/pairScreen/default-tv-display.svg";
import pixiDevice from "../NumberOfScreensStep/assets/pixi-device.svg";
import {
  DiscountLineItemPrice,
  LineItem,
  LineItemCaption,
  LineItemColumn,
  LineItemImage,
  LineItemImageWrap,
  LineItemPrice,
  LineItemTitle,
  OrderSummaryCardStyled,
  PurchaseError,
  PurchaseRow,
  SummaryDescription,
  SummaryDivider,
  SummaryHeader,
  SummaryHeading,
  ShippingLineItemPrice,
  SummaryItems,
  TaxAmount,
  TaxErrorText,
  TaxLine,
  TaxRows,
  TaxStatusRow,
  TotalRow,
  TotalValue,
} from "./styles";

/** A tax line for display: provider label + formatted (currency) amount. */
export interface TaxLineView {
  label: string;
  amount: string;
}

export interface OrderSummaryProps {
  planName: string;
  licenses: number;
  licensesPrice: string;
  freeDevices: boolean;
  deviceCount: number;
  /**
   * Formatted zero amount (e.g. "$0") shown as the free devices' cost — mirrors
   * the Device review card (GRWBKK-1739 follow-up).
   */
  deviceCostPrice: string;
  /**
   * Formatted discount amount (positive, e.g. "$144") — rendered as a green
   * negative line under the licences price. Omitted → no discount line
   * (free-device / no-deal flows).
   */
  discountPrice?: string;
  shippingPrice: string;
  /**
   * Tax lines for the billing location (GRWBKK-1745). One row per line — a
   * location can return a country-level tax plus a state/regional tax. Empty
   * (default) → no tax section and the Total equals the subtotal.
   */
  taxLines?: TaxLineView[];
  /** True while the tax estimate is loading — hide the Total, block purchase. */
  taxLoading?: boolean;
  /** True when the tax estimate failed — show a message and block purchase. */
  taxError?: boolean;
  /** Formatted grand total; tax-inclusive once the estimate has resolved. */
  total: string;
  purchasing: boolean;
  onPurchase: () => void;
  /** Charge/SCA failure message from usePurchase (useError); empty when none. */
  error?: string;
}

export const OrderSummary = ({
  planName,
  licenses,
  licensesPrice,
  freeDevices,
  deviceCount,
  deviceCostPrice,
  discountPrice,
  shippingPrice,
  taxLines = [],
  taxLoading = false,
  taxError = false,
  total,
  purchasing,
  onPurchase,
  error,
}: OrderSummaryProps) => {
  // The tax block (between the shipping subtotal and the Total, bracketed by
  // dividers) is shown whenever there's something to say about tax: a loading
  // state, a failure, or one or more resolved tax lines.
  const hasTaxSection = taxLoading || taxError || taxLines.length > 0;
  return (
    <OrderSummaryCardStyled data-testid="checkout-order-summary">
      <SummaryHeader>
        <SummaryHeading>
          <FormattedMessage
            id="pixi_wizard.checkout.summary.title"
            defaultMessage="Order Summary"
          />
        </SummaryHeading>
        <SummaryDescription>
          <FormattedMessage
            id="pixi_wizard.checkout.summary.description"
            defaultMessage="Review the items in your order and confirm your subscription details. Your total includes all selected licences, devices, discounts, and shipping charges."
          />
        </SummaryDescription>
      </SummaryHeader>

      <SummaryItems>
        <LineItem>
          <LineItemImageWrap>
            <LineItemImage src={tvDisplay} alt="" aria-hidden />
          </LineItemImageWrap>
          <LineItemColumn>
            <LineItemCaption>
              <FormattedMessage
                id="pixi_wizard.checkout.summary.plan_caption"
                defaultMessage="{plan} Plan"
                values={{ plan: planName }}
              />
            </LineItemCaption>
            <LineItemTitle data-testid="summary-licenses">
              <FormattedMessage
                id="pixi_wizard.checkout.summary.licenses"
                defaultMessage="x{count} {count, plural, one {Licence} other {Licences}}"
                values={{ count: licenses }}
              />
            </LineItemTitle>
            <LineItemPrice>{licensesPrice}</LineItemPrice>
            {discountPrice && (
              <DiscountLineItemPrice data-testid="summary-discount">
                -{discountPrice}
              </DiscountLineItemPrice>
            )}
          </LineItemColumn>
        </LineItem>

        {freeDevices && (
          <LineItem data-testid="summary-devices">
            <LineItemImageWrap>
              <LineItemImage src={pixiDevice} alt="" aria-hidden />
            </LineItemImageWrap>
            <LineItemColumn>
              <LineItemTitle>
                <FormattedMessage
                  id="pixi_wizard.checkout.summary.free_devices"
                  defaultMessage="x{count} {count, plural, one {Free PIXI Device} other {Free PIXI Devices}}"
                  values={{ count: deviceCount }}
                />
              </LineItemTitle>
              <LineItemPrice data-testid="summary-device-price">
                {deviceCostPrice}
              </LineItemPrice>
              <ShippingLineItemPrice data-testid="summary-shipping">
                <FormattedMessage
                  id="pixi_wizard.checkout.summary.shipping"
                  defaultMessage="Shipping: +{amount}"
                  values={{ amount: shippingPrice }}
                />
              </ShippingLineItemPrice>
            </LineItemColumn>
          </LineItem>
        )}
      </SummaryItems>

      <SummaryDivider />

      {hasTaxSection && (
        <>
          {taxLoading ? (
            <TaxStatusRow data-testid="summary-tax-loading">
              <Loader inline active size="small" />
            </TaxStatusRow>
          ) : taxError ? (
            <TaxErrorText data-testid="summary-tax-error">
              <FormattedMessage
                id="pixi_wizard.checkout.summary.tax_error"
                defaultMessage="We couldn't calculate the tax for your billing location. Please review your billing details and try again."
              />
            </TaxErrorText>
          ) : (
            <TaxRows>
              {taxLines.map((tax, index) => (
                <TaxLine
                  key={`${tax.label}-${index}`}
                  data-testid="summary-tax"
                >
                  <FormattedMessage
                    id="pixi_wizard.checkout.summary.tax"
                    defaultMessage="{label}: {amount}"
                    values={{
                      label: tax.label,
                      amount: <TaxAmount>+{tax.amount}</TaxAmount>,
                    }}
                  />
                </TaxLine>
              ))}
            </TaxRows>
          )}
          <SummaryDivider />
        </>
      )}

      <TotalRow>
        <LineItemColumn>
          <LineItemTitle>
            <FormattedMessage
              id="pixi_wizard.checkout.summary.total"
              defaultMessage="Total"
            />
          </LineItemTitle>
          {taxLoading ? (
            <Loader
              inline
              active
              size="small"
              data-testid="summary-total-loading"
            />
          ) : (
            <TotalValue data-testid="summary-total">{total}</TotalValue>
          )}
        </LineItemColumn>
      </TotalRow>

      <SummaryDivider />

      {error && (
        <PurchaseError data-testid="checkout-purchase-error">
          {error}
        </PurchaseError>
      )}

      <PurchaseRow>
        <Button
          primary
          onClick={onPurchase}
          disabled={purchasing || taxLoading || taxError}
          data-testid="checkout-purchase"
        >
          {purchasing ? (
            <Loader
              inline
              active
              size="small"
              data-testid="checkout-purchase-loader"
            />
          ) : (
            <FormattedMessage
              id="pixi_wizard.checkout.purchase"
              defaultMessage="Purchase"
            />
          )}
        </Button>
      </PurchaseRow>
    </OrderSummaryCardStyled>
  );
};
